<template>
	<div v-if="enabled" class="flex w-full flex-col gap-2">
		<label :for="fieldId" class="text-sm text-gray-700 font-bold">
			{{ $t("checkout.comment.label") }}
			<span v-if="required" class="text-red-600" aria-hidden="true">*</span>
		</label>
		<p v-if="helpText" :id="helpId" class="text-sm text-gray-500">
			{{ helpText }}
		</p>
		<textarea
			:id="fieldId"
			v-model="value"
			:maxlength="maxLength"
			:required="required"
			:aria-describedby="describedBy || undefined"
			:aria-invalid="showError || undefined"
			rows="3"
			class="w-full resize-y border border-gray-300 rounded p-3 text-gray-700 focus:border-gray-500 focus:outline-none"
			@blur="handlePersist"
		></textarea>
		<div class="flex justify-between gap-2 text-xs text-gray-500">
			<span v-if="showError" :id="errorId" class="text-red-600">
				{{ $t("checkout.comment.required_error") }}
			</span>
			<span class="ml-auto tabular-nums"
				>{{ value.length }}/{{ maxLength }}</span
			>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import type { Basket } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";

// [NF-385] Optional free-text customer comment, saved to the Norce basket
// `Comment` field (which already flows to UpdateBasket2). Self-contained on the
// cart store so it drops into Checkout.vue as a single tag; it renders nothing
// unless enabled per client via env (public.checkout.comment).

const { t } = useI18n();
const { $toast } = useNuxtApp();
const { enabled, required, maxLength, satisfied, pendingSave } =
	useCheckoutComment();

const cartStore = useCartStore();
const { basket } = storeToRefs(cartStore);
const norceStore = useNorceStore();
const { updateBasket } = useNorcePwa();

// When required, the payment step stays gated until a non-empty comment is saved.
const updateSatisfied = (saved: string) => {
	satisfied.value = !required || saved.trim().length > 0;
};

const fieldId = "checkout-comment";
const helpId = "checkout-comment-help";
const errorId = "checkout-comment-error";

const touched = ref(false);

// Strips control characters while keeping newlines, collapses CRLF, and clamps
// to maxLength. The textarea `maxlength` is a soft guard only (paste and IME can
// still exceed it), so this clamp is the real limit. HTML is left intact on
// purpose: escaping is the email template's job (Norce Application Mail XSLT
// escapes by default), and mangling angle brackets here would corrupt legit text.
const sanitize = (raw: string): string => {
	const cleaned = raw
		.replace(/\r\n?/g, "\n")
		// Strip control chars (keep \t \n \r) so pasted junk can't reach Norce or
		// the email template; matching them is the whole point here.
		// eslint-disable-next-line no-control-regex
		.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
	// Clamp by code point, not UTF-16 unit, so the limit can't split an emoji and
	// leave a lone surrogate — which is not a valid XML char and breaks the XSLT
	// mail / ERP export.
	return Array.from(cleaned).slice(0, maxLength).join("");
};

const value = ref(sanitize(basket.value?.Comment ?? ""));
let persisted = sanitize(basket.value?.Comment ?? "").trim();
updateSatisfied(persisted);

const helpText = computed(() => {
	// A required field must not read "Optional ..."; use the required variant.
	const key = required
		? "checkout.comment.help_required"
		: "checkout.comment.help";
	const text = t(key);
	// An empty string (or a client blanking the key) means "no help text".
	return text && text !== key ? text : "";
});

const showError = computed(
	() => required && touched.value && value.value.trim().length === 0
);

const describedBy = computed(() =>
	[helpText.value ? helpId : "", showError.value ? errorId : ""]
		.filter(Boolean)
		.join(" ")
);

// Reseed only on a basket SWITCH (different Id). Our own save and the checkout's
// order-sync both replace the basket object with the same Id, so keying on Id
// keeps those from clobbering what the shopper is typing.
watch(
	() => basket.value?.Id,
	() => {
		const next = sanitize(basket.value?.Comment ?? "");
		value.value = next;
		persisted = next.trim();
		touched.value = false;
		updateSatisfied(persisted);
	}
);

const save = async (clean: string, basketId: number) => {
	// norce-pwa's update-basket handler now fills OrderReference/DiscountCode/Info
	// server-side when the body omits them, so a bare { Id, Comment } no longer
	// wipes them. Write through the pwa call (not cartStore.updateBasket) and patch
	// only the store's Comment, so we don't replace the whole basket and fire
	// Checkout's watcher — which would run a full order resync and suspend the
	// payment/shipping widgets just as the shopper reaches them.
	const updated = (await updateBasket(norceStore.storeContext, {
		Id: basketId,
		Comment: clean
	} as Basket)) as Basket | undefined;
	if (basket.value) basket.value.Comment = updated?.Comment ?? clean;
};

const handlePersist = async () => {
	touched.value = true;
	const clean = sanitize(value.value).trim();
	// Reflect sanitisation back into the field so what is saved is what is shown.
	if (clean !== value.value) value.value = clean;
	if (!basket.value?.Id) return;
	if (clean === persisted) return;
	const basketId = basket.value.Id;
	const request = save(clean, basketId);
	// Expose the in-flight save so a place-order flow can await it (ManualInvoice).
	pendingSave.value = request;
	try {
		await request;
		persisted = clean;
		updateSatisfied(clean);
	} catch (error) {
		console.error("[CheckoutComment] failed to save comment:", error);
		// Keep the field marked unsaved (persisted unchanged) so the next blur
		// retries, and tell the shopper rather than failing silently.
		$toast.error(t("checkout.comment.save_failed"));
	} finally {
		if (pendingSave.value === request) pendingSave.value = null;
	}
};
</script>
